import * as React from "react";
import Link from "next/link";
import { ArrowUpRight, MapPin } from "lucide-react";

import type { LocationFacet } from "@/lib/job-board/types";
import { exactLocationCards, locationJobsUrl, matchingJobCountLabel } from "@/lib/job-board/location-normalization";
import { Button } from "@/components/ui/button";
import LocationSkeleton from "./location-skeleton";

const INITIAL_LOCATION_COUNT = 8;

type Props = {
  initialLocations?: LocationFacet[];
  initialError?: boolean;
};

export default function JobsByLocationSection({ initialLocations, initialError = false }: Props) {
  const [locations, setLocations] = React.useState<LocationFacet[]>(initialLocations || []);
  const [isLoading, setIsLoading] = React.useState(!initialLocations && !initialError);
  const [hasError, setHasError] = React.useState(initialError);
  const [isExpanded, setIsExpanded] = React.useState(false);

  const locationCards = React.useMemo(() => exactLocationCards(locations), [locations]);
  const visibleLocations = isExpanded ? locationCards : locationCards.slice(0, INITIAL_LOCATION_COUNT);

  const loadLocations = React.useCallback(async () => {
    setIsLoading(true);
    setHasError(false);
    try {
      const response = await fetch("/api/facets/locations");
      if (!response.ok) throw new Error("Unable to load locations");
      const data: unknown = await response.json();
      setLocations(Array.isArray(data) ? (data as LocationFacet[]) : []);
    } catch {
      setHasError(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  React.useEffect(() => {
    if (!initialLocations && !initialError) void loadLocations();
  }, [initialError, initialLocations, loadLocations]);

  return (
    <section className="container py-10" aria-labelledby="jobs-by-location-heading" aria-busy={isLoading || undefined}>
      <div className="mb-6 max-w-2xl">
        <p className="text-eyebrow mb-2">Explore by location</p>
        <h2 id="jobs-by-location-heading" className="mb-2">
          Find Jobs in Your City
        </h2>
        <p className="m-0 text-sm text-muted-foreground sm:text-[15px]">
          Discover career opportunities across India&apos;s leading employment hubs and beyond.
        </p>
      </div>

      {isLoading ? <LocationSkeleton /> : null}

      {!isLoading && hasError ? (
        <div className="recent-job-card px-5 py-8 text-center" role="status" aria-live="polite">
          <p className="m-0 text-sm text-muted-foreground">We couldn’t load job locations right now.</p>
          <Button type="button" variant="outline" size="sm" className="mt-4" onClick={loadLocations}>
            Try again
          </Button>
        </div>
      ) : null}

      {!isLoading && !hasError && locationCards.length === 0 ? (
        <div className="recent-job-card px-5 py-8 text-center">
          <p className="m-0 text-sm text-muted-foreground">No locations available right now.</p>
        </div>
      ) : null}

      {!isLoading && !hasError && locationCards.length > 0 ? (
        <>
          <div className="grid grid-cols-1 gap-4 min-[420px]:grid-cols-2 lg:grid-cols-4">
            {visibleLocations.map((location) => (
              <Link
                key={location.id}
                href={locationJobsUrl(location.source)}
                className="recent-job-card group flex min-h-[94px] items-center gap-3 p-4 no-underline outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 motion-reduce:transition-none"
                aria-label={`View ${matchingJobCountLabel(location.count)} in ${location.label}`}
              >
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <MapPin className="h-[18px] w-[18px]" aria-hidden="true" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-semibold text-foreground sm:text-[15px]">{location.label}</span>
                  <span className="text-count mt-0.5 block">{matchingJobCountLabel(location.count)}</span>
                </span>
                <ArrowUpRight className="h-4 w-4 shrink-0 text-muted-foreground transition-colors group-hover:text-primary" aria-hidden="true" />
              </Link>
            ))}
          </div>

          {locationCards.length > INITIAL_LOCATION_COUNT ? (
            <div className="mt-6 text-center">
              <Button type="button" variant="outline" onClick={() => setIsExpanded((expanded) => !expanded)}>
                {isExpanded ? "Show Less" : "View All Locations"}
              </Button>
            </div>
          ) : null}
        </>
      ) : null}
    </section>
  );
}
